import useStyles from 'isomorphic-style-loader/useStyles';
import { Flame } from 'lucide-react';
import { Popup } from 'semantic-ui-react';

import { AnchoreLink, AnchoreWordmark } from '@components/anchore-link';
import {
  Breadcrumb,
  useCurrentUrlCopyLink,
} from '@components/breadcrumb/breadcrumb';
import Page from '@components/page';
import { getPrimaryCvss } from '@components/severity';
import { VulnerabilityIndicators } from '@components/vulnerability-indicators';

import {
  builtAtSuffix,
  getPrimaryDetail,
  noImpactedVersionsMessage,
} from '../quick-search.utils';

import { ImpactStatusLabel } from './impact-status-label';
import type { VulnerabilityLookupItem } from './vulnerability-search.models';
import s from './vulnerability-search-results-page-header.scss';

export interface VulnerabilitySearchResultsPageHeaderProps {
  vulnerability: VulnerabilityLookupItem;
  /**
   * Whether any application version is affected. Left `undefined` while the
   * impact search is loading or has errored, in which case no pill is shown.
   */
  impacted?: boolean;
  /**
   * The vulnerability database build date, shown in the not-impacted tooltip.
   */
  builtAt?: string | null;
}

export const VulnerabilitySearchResultsPageHeader = ({
  vulnerability,
  impacted,
  builtAt,
}: VulnerabilitySearchResultsPageHeaderProps) => {
  useStyles(s);

  const { vulnerabilityId } = vulnerability;
  const detail = getPrimaryDetail(vulnerability);
  const cvssAssessment = getPrimaryCvss(detail?.cvssAssessments ?? []) ?? null;

  const impactTooltip = impacted
    ? `This vulnerability is in your environment${builtAtSuffix(builtAt)}. Impacted application versions may be hidden by currently applied filters`
    : noImpactedVersionsMessage(builtAt);

  const copyLink = useCurrentUrlCopyLink('Copy link to this vulnerability');

  return (
    <Page.Header
      sticky
      className={s.root}
      breadcrumb={
        <Breadcrumb
          items={[
            { text: 'Search' },
            { text: 'Vulnerabilities' },
            { text: vulnerabilityId },
          ]}
          copyLink={copyLink}
        />
      }
      icon={<Flame />}
      title={
        <span className={s.title}>
          <span className={s.cve}>{vulnerabilityId}</span>
          <span className={s.dbLink}>
            <AnchoreLink vulnerabilityId={vulnerabilityId}>
              <span className={s.dbLinkLabel}>View</span>
              <AnchoreWordmark />
              <span className={s.dbLinkLabel}>
                Vulnerability Database for more details
              </span>
            </AnchoreLink>
          </span>
        </span>
      }
      subtitle={detail?.description ?? undefined}
      extra={
        <div className={s.metrics}>
          <VulnerabilityIndicators
            cvssAssessment={cvssAssessment}
            epssPercentile={detail?.epssPercentile ?? null}
            epssScore={detail?.epssScore ?? null}
            kev={!!detail?.kev}
            vulnerabilityId={vulnerabilityId}
          />

          {impacted !== undefined && (
            <Popup
              position="top center"
              content={impactTooltip}
              trigger={
                <span className={s.impactTrigger}>
                  <ImpactStatusLabel impacted={impacted} />
                </span>
              }
            />
          )}
        </div>
      }
    />
  );
};

export default VulnerabilitySearchResultsPageHeader;
